import { saveAs } from 'file-saver';
import React, { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { Button } from '~/eds/src/components/Button/Button';
import { Checkbox } from '~/eds/src/components/Checkbox/Checkbox';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Panel } from '~/eds/src/components/Panel/Panel';
import { Text } from '~/eds/src/components/Text/Text';
import { FileMimeType } from '~/enums/FileMimeType';
import { HttpStatusCodeType } from '~/enums/HttpStatusCodeType';
import { SearchQuery } from '~/features/advanced-search/types';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { useFormatDate } from '~/i18n/hooks/useFormatDate/useFormatDate';
import { useFormatNumber } from '~/i18n/hooks/useFormatNumber/useFormatNumber';
import { api } from '~/redux/api/createApi';
import { SearchType } from '~/redux/api/methods/evisearch';
import { Nullable } from '~/types/Nullable';
import { ERROR, SUCCESS } from '~/types/toast.types';

import { showToast } from '../EcToast/showToast';
import { defaultSelectedOptions } from './constants';
import { useExportSettings } from './useExportSettings/useExportSettings';

type Props = {
  query: SearchQuery;
  entity?: SearchType;
  isVisible: boolean;
  totalDocs?: number;
  totalDupsAndDocs?: number;
  selectedFieldIds?: string[];
  onHide: () => void;
};

const getFields = (selectedFieldIds: string[], isPreSig: boolean) => {
  if (isPreSig) return selectedFieldIds;
  const selectedFieldsSets = new Set([
    ...defaultSelectedOptions,
    ...selectedFieldIds,
  ]);
  return [...selectedFieldsSets];
};

export const ExportExcel = ({
  query,
  entity,
  isVisible,
  totalDocs = 0,
  totalDupsAndDocs = 0,
  selectedFieldIds = [],
  onHide,
}: Props) => {
  const intl = useIntl();
  const formatDate = useFormatDate();
  const formatNumber = useFormatNumber();
  const isPresign = entity === 'ticket' || entity === 'workflow';
  const [localSettings, setLocalSettings] = useState<
    Record<string, Nullable<string[]>>
  >({
    fields: getFields(selectedFieldIds, isPresign),
  });
  const [includeDuplicates, setIncludeDuplicates] = useState(false);
  const [includeContractRecordUniqueId, setIncludeContractRecordUniqueId] =
    useState(false);
  const isContractRecordUniqueIdEnabled = useFlag(
    FlagType.WSSIntegrationM6ProcurementMigration,
  );
  const [selectedTab, setSelectedTab] = useState('all');

  const [exportExcel, exportResult] =
    api.endpoints[isPresign ? 'exportExcelV3' : 'exportExcelV2'].useMutation();
  const { isLoading: isExporting, isError, isSuccess, data } = exportResult;
  const {
    data: exportSettings,
    isFetching: isFetchingOptions,
    isError: isErrorOptions,
  } = api.endpoints[
    isPresign ? 'getSearchExportOptionsV2' : 'getSearchExportOptions'
  ].useQuery({ query: query as any, type: entity! }, { skip: !isVisible });

  const { fields, clauses, tables } = localSettings;
  const selectedItemsCount =
    (fields?.length || 0) + (clauses?.length || 0) + (tables?.length || 0);

  const metadata = useMemo(
    () => [
      {
        label: intl.formatMessage({
          id: 'ExportExcel.selectedItems',
          defaultMessage: 'Selected Items',
          description: 'Label for selected items count',
        }),
        value: selectedItemsCount,
        hidden: false,
      },
      {
        label: isPresign
          ? intl.formatMessage({
              id: 'ExportExcel.totalItems',
              defaultMessage: 'Total Items',
              description: 'Label for total items count',
            })
          : intl.formatMessage({
              id: 'ExportExcel.totalDocuments',
              defaultMessage: 'Total Documents',
              description: 'Label for total documents count',
            }),
        value: totalDocs,
        hidden: false,
      },
      {
        label: intl.formatMessage({
          id: 'ExportExcel.totalDuplicates',
          defaultMessage: 'Total Duplicates',
          description: 'Label for total duplicates count',
        }),
        value: totalDupsAndDocs - totalDocs,
        hidden: isPresign,
      },
      {
        label: intl.formatMessage({
          id: 'ExportExcel.includingDuplicates',
          defaultMessage: 'Including Duplicates',
          description: 'Label for including duplicates count',
        }),
        value: totalDupsAndDocs,
        hidden: isPresign,
      },
    ],
    [selectedItemsCount, totalDocs, totalDupsAndDocs],
  );

  const handleHide = () => {
    onHide();
    setLocalSettings({
      fields: getFields(selectedFieldIds, isPresign),
      clauses: [],
      tables: [],
    });
    setIncludeDuplicates(false);
    setIncludeContractRecordUniqueId(false);
  };

  const submitExportRequest = () => {
    exportExcel({
      fields,
      query: query as any, //TODO: Change type to QueryV3
      type: entity!,
      clauses,
      tables,
      includeDuplicates,
      includeContractRecordUniqueId,
    });
  };

  useEffect(() => {
    if (selectedFieldIds.length) {
      setLocalSettings({ fields: getFields(selectedFieldIds, isPresign) });
    }
  }, [selectedFieldIds]);

  useEffect(() => {
    if (isSuccess && data) {
      const excelMessage = intl.formatMessage({
        id: 'ExportExcel.exportStartedMessage',
        defaultMessage: 'Your results have started exporting. ',
        description: 'Message when export has started',
      });
      if (data.status === HttpStatusCodeType.Ok) {
        showToast(SUCCESS, excelMessage);
        const blob = new Blob([data.data], { type: FileMimeType.Xlsx });
        const dateString = formatDate(new Date(), 'utc');
        saveAs(blob, `${dateString} Evisort Export.xlsx`);
      } else if (data.status === HttpStatusCodeType.Accepted) {
        showToast(
          SUCCESS,
          excelMessage +
            intl.formatMessage({
              id: 'ExportExcel.emailNotificationMessage',
              defaultMessage:
                'You will receive an email when the export is complete.',
              description:
                'Message about email notification for export completion',
            }),
        );
      }
      showToast(
        SUCCESS,
        intl.formatMessage({
          id: 'ExportExcel.exportSuccessMessage',
          defaultMessage: 'Successfully exported excel file',
          description: 'Success message for excel export',
        }),
      );

      handleHide();
    }
  }, [isSuccess, data]);

  useEffect(() => {
    if (isError) {
      showToast(
        ERROR,
        intl.formatMessage({
          id: 'ExportExcel.exportErrorMessage',
          defaultMessage: 'Error exporting excel file. Please try again later.',
          description: 'Error message for failed excel export',
        }),
      );
    }
  }, [isError]);

  useEffect(() => {
    if (isErrorOptions) {
      showToast(
        ERROR,
        intl.formatMessage({
          id: 'ExportExcel.optionsErrorMessage',
          defaultMessage:
            'Error on retrieving export options. Try again later.',
          description: 'Error message for failed export options retrieval',
        }),
      );
    }
  }, [isErrorOptions]);

  const footer = (
    <Layout direction="column">
      <Layout
        justify={isPresign ? 'flex-start' : 'space-between'}
        p={4}
        borderTop="border"
      >
        {metadata
          .filter((metadataItem) => !metadataItem.hidden)
          .map((metadataItem) => (
            <Layout
              key={metadataItem.label}
              direction="column"
              mr={isPresign ? 8 : 0}
            >
              <Text variant="body">{metadataItem.label}:</Text>
              <Text variant="body-bold">
                {formatNumber(metadataItem.value, { decimal: 0 })}
              </Text>
            </Layout>
          ))}
      </Layout>
      <Layout
        p={4}
        direction="row"
        align="center"
        justify="flex-end"
        borderTop="border"
      >
        {!isPresign && (
          <Checkbox
            name="duplicates"
            value={includeDuplicates}
            onChange={(value) => setIncludeDuplicates(!!value)}
            option={{
              label: intl.formatMessage({
                id: 'ExportExcel.includeDuplicates',
                defaultMessage: 'Include duplicates',
                description: 'Checkbox label for including duplicates',
              }),
              value: includeDuplicates,
            }}
          />
        )}

        {!isPresign && isContractRecordUniqueIdEnabled && (
          <Checkbox
            name="contractRecordUniqueId"
            value={includeContractRecordUniqueId}
            onChange={(value) => setIncludeContractRecordUniqueId(!!value)}
            option={{
              label: intl.formatMessage({
                id: 'ExportExcel.includeContractRecordUniqueId',
                defaultMessage: 'Include Contract ID',
                description:
                  'Checkbox label for including contract record unique ID column in export',
              }),
              value: includeContractRecordUniqueId,
            }}
          />
        )}

        <Layout ml={4}>
          <Button
            isLoading={isFetchingOptions || isExporting}
            disabled={isFetchingOptions || isExporting || isErrorOptions}
            text={intl.formatMessage({
              id: 'ExportExcel.exportButton',
              defaultMessage: 'Export Excel',
              description: 'Button text for exporting to Excel',
            })}
            variant="primary"
            onClick={submitExportRequest}
          />
        </Layout>
      </Layout>
    </Layout>
  );
  const tabs = useExportSettings({
    data: exportSettings,
    settings: localSettings,
    selectedFieldIds: localSettings.fields ?? [],
    onChange: setLocalSettings,
  });
  const filteredTabs = isPresign
    ? // Show only "all" tab if presign
      [tabs[0]]
    : tabs.filter((tab) => tab.items?.length);

  const panelTabs = {
    selectedTab,
    tabs: filteredTabs,
    onSelectTab: setSelectedTab,
  };

  if (!isVisible) return null;

  return (
    <Panel
      title={intl.formatMessage({
        id: 'ExportExcel.panelTitle',
        defaultMessage: 'Export Results',
        description: 'Panel title for export results',
      })}
      hidden={{ isHidden: false, onHide: handleHide }}
      footer={{ children: footer }}
      tabs={panelTabs}
      loadingContent={{
        isLoading: isFetchingOptions,
      }}
      enableBackdrop
      mode="floating"
      width="l"
      enableContentPadding={false}
    />
  );
};
